import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { createPortal } from "react-dom";
import { ArrowLeft, ChevronDown, ChevronRight, Mic, Pause, Play, Search, Settings2, Sparkles, Upload, X, Zap } from "lucide-react";
import { Modal } from "../ui/Modal";
import BottomDrawer from "../ui/BottomDrawer";
import EmbeddedVoicePicker from "./EmbeddedVoicePicker";
import VoiceCloneView from "./VoiceCloneView";
import { SearchableSelect } from "../ui/SearchableSelect";
import { useAudioRecorder } from "../../hooks/useAudioRecorder";
import { useLanguages } from "../../http-hooks/ai-voices/useLanguages";
import { useGetPresignedUrl, uploadToS3 } from "../../http-hooks/ai-voices/useClonedVoice";
import { useCreateVoiceDesign } from "../../http-hooks/ai-voices/useVoiceDesign";
import { useCreateVoiceMix } from "../../http-hooks/ai-voices/useVoiceMix";
import FeatureCreditCost from "../plans/FeatureCreditCost";
import { useFeatureCreditGate } from "../../http-hooks/organisation/useFeatureCreditEstimate";
import useIsMobile from "../../hooks/useIsMobile";
import { C } from "../../styles/tokens";
import { getVoicePreviewUrl } from "../../utils/voicePreview";
import { addMixPanelEvent } from "../../store/actions";

const OPTIONS = [
  {
    id: "design",
    icon: <Sparkles size={16} color={C.v500} />,
    iconBg: "#EEF2FF",
    title: "Voice Design",
    description: "Design an entirely new voice from a text prompt.",
    detailDescription:
      "Describe the voice you want — age, tone, accent, emotion. Pick a reference voice for inspiration.",
    time: "Less than a minute",
    placeholder: "A young male with a clear, warm voice...",
  },
  {
    id: "clone",
    icon: <Zap size={16} color="#F59E0B" />,
    iconBg: "#FFFBEB",
    title: "Instant Voice Clone",
    description: "Clone your voice with only 10 seconds of audio.",
    detailDescription: "Upload or record a short audio sample to clone a voice instantly.",
    time: "Less than a minute",
    placeholder: "",
  },
  {
    id: "remix",
    icon: <Sparkles size={16} color={C.v500} />,
    iconBg: "#EEF2FF",
    title: "Voice Remixing",
    description: "Blend two voices together with similarity controls to create a new one.",
    detailDescription: "Select two voices and adjust the blend ratio to create a new unique voice.",
    time: "Less than a minute",
    placeholder: "",
  },
];

// Deduplicated language list — design/mix voices need a language_code too.
const VOX_LANGUAGES = {
  zh: 'Chinese', en: 'English', ja: 'Japanese', ko: 'Korean', de: 'German',
  fr: 'French', ru: 'Russian', pt: 'Portuguese', es: 'Spanish', it: 'Italian',
  ar: 'Arabic', da: 'Danish', el: 'Greek', fi: 'Finnish', he: 'Hebrew',
  hi: 'Hindi', id: 'Indonesian', km: 'Khmer', lo: 'Lao', ms: 'Malay',
  my: 'Burmese', nl: 'Dutch', no: 'Norwegian', pl: 'Polish', sv: 'Swedish',
  sw: 'Swahili', th: 'Thai', tl: 'Filipino', tr: 'Turkish', vi: 'Vietnamese',
};

// Voice Remixing only supports this subset of languages.
const MIX_LANGUAGES = new Set(["zh", "en", "ja", "ko", "de", "fr", "ru", "pt", "es", "it"]);

function useQwenLanguageOptions({ mixOnly = false } = {}) {
  const { data: languages } = useLanguages();
  return useMemo(() => {
    if (!languages) return [];
    const seen = new Set();
    const result = [];
    for (const key of Object.keys(languages)) {
      const prefix = key.split("-")[0].toLowerCase();
      if (mixOnly && !MIX_LANGUAGES.has(prefix)) continue;
      if (VOX_LANGUAGES[prefix] && !seen.has(prefix)) {
        seen.add(prefix);
        result.push({ code: languages[key].code || key, name: VOX_LANGUAGES[prefix] });
      }
    }
    return result;
  }, [languages, mixOnly]);
}

function LanguageSelect({ value, onChange, langOptions }) {
  const options = langOptions.length > 0
    ? langOptions.map((l) => ({ value: l.code, label: l.name }))
    : [{ value: "en-US", label: "English (US)" }];
  return (
    <div>
      <label style={sectionLabelStyle}>Language</label>
      <SearchableSelect selected={value} onChange={onChange} options={options} placeholder="Select language" />
    </div>
  );
}

// ── Option list ───────────────────────────────────────────────────────────────

export function OptionList({ onSelect }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {OPTIONS.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onSelect(opt.id)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 6,
            width: "100%",
            textAlign: "left",
            padding: "16px 18px",
            borderRadius: 12,
            border: `1.5px solid ${C.n200}`,
            background: "#fff",
            cursor: "pointer",
            transition: "border-color .15s, box-shadow .15s",
            fontFamily: "inherit",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = C.v500;
            e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99,102,241,.08)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = C.n200;
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                background: opt.iconBg,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {opt.icon}
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>
              {opt.title}
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: C.n500, lineHeight: 1.5 }}>
            {opt.description}
          </p>
          <span
            style={{
              fontSize: 11.5, fontWeight: 500, color: C.n500,
              background: C.n100, borderRadius: 20, padding: "3px 10px",
            }}
          >
            {opt.time}
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Voice Design settings ─────────────────────────────────────────────────────

const VOICE_SETTINGS = [
  {
    key: "loudness",
    label: "Loudness",
    options: [
      { label: "Very Quiet",  value: 0.0 },
      { label: "Quiet",       value: 0.25 },
      { label: "Normal",      value: 0.5 },
      { label: "Loud",        value: 0.75 },
      { label: "Very Loud",   value: 1.0 },
    ],
    default: 0.5,
  },
  {
    key: "quality",
    label: "Quality",
    options: [
      { label: "Most Varied",      value: 0.0 },
      { label: "Varied",           value: 0.25 },
      { label: "Balanced",         value: 0.5 },
      { label: "Consistent",       value: 0.75 },
      { label: "Most Consistent",  value: 1.0 },
    ],
    default: 1.0,
  },
  {
    key: "guidance",
    label: "Guidance",
    options: [
      { label: "Very Creative", value: 0.0 },
      { label: "Creative",      value: 0.25 },
      { label: "Balanced",      value: 0.5 },
      { label: "Precise",       value: 0.75 },
      { label: "Very Precise",  value: 1.0 },
    ],
    default: 0.25,
  },
];

const DEFAULT_SETTINGS = Object.fromEntries(
  VOICE_SETTINGS.map((s) => [s.key, s.default])
);

function labelForValue(setting, value) {
  return setting.options.find((o) => o.value === value)?.label ?? "";
}

function SettingsPopover({ settings, onChange, anchorRect, onClose }) {
  const [activeKey, setActiveKey] = useState(null);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  if (!anchorRect) return null;

  // Position above the anchor button, aligned to its left edge
  const popoverWidth = 220;
  const popoverHeight = activeKey ? 170 : 196;
  const top = anchorRect.top - popoverHeight - 8;
  const left = anchorRect.left;

  const activeSetting = VOICE_SETTINGS.find((s) => s.key === activeKey);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        top,
        left,
        width: popoverWidth,
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 4px 24px rgba(0,0,0,.12), 0 0 0 1px rgba(0,0,0,.06)",
        zIndex: 2000,
        overflow: "hidden",
      }}
    >
      {activeKey ? (
        /* ── Option list for one setting ── */
        <>
          <button
            type="button"
            onClick={() => setActiveKey(null)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              width: "100%", padding: "10px 14px",
              background: "none", border: "none", borderBottom: `1px solid ${C.n100}`,
              fontSize: 12, fontWeight: 600, color: C.n500, cursor: "pointer",
            }}
          >
            <ArrowLeft size={12} />
            {activeSetting.label}
          </button>
          {activeSetting.options.map((opt) => {
            const selected = settings[activeKey] === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => { onChange(activeKey, opt.value); setActiveKey(null); }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  width: "100%", padding: "9px 14px",
                  background: selected ? "#F5F3FF" : "none",
                  border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: selected ? 600 : 400,
                  color: selected ? C.v500 : C.n900,
                }}
              >
                {opt.label}
                {selected && <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.v500 }} />}
              </button>
            );
          })}
        </>
      ) : (
        /* ── Root: all three settings ── */
        VOICE_SETTINGS.map((s, i) => (
          <div key={s.key}>
            {i > 0 && <div style={{ height: 1, background: C.n100, margin: "0 14px" }} />}
            <div style={{ padding: "8px 14px 2px", fontSize: 11, fontWeight: 600, color: "#9CA3AF", letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {s.label}
            </div>
            <button
              type="button"
              onClick={() => setActiveKey(s.key)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "6px 14px 10px",
                background: "none", border: "none", cursor: "pointer",
                fontSize: 13, fontWeight: 500, color: C.n900,
              }}
            >
              {labelForValue(s, settings[s.key])}
              <ChevronRight size={13} color="#9CA3AF" />
            </button>
          </div>
        ))
      )}
    </div>,
    document.body
  );
}

// ── Voice reference drawer ────────────────────────────────────────────────────

function VoiceReferenceDrawer({ isOpen, onClose, selectedVoice, onVoiceSelect, isMobile, emotionalOnly = true }) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (isMobile) {
    return (
      <BottomDrawer isOpen={isOpen} onClose={onClose} title="Voice reference">
        <EmbeddedVoicePicker
          selectedVoice={selectedVoice}
          onVoiceSelect={(v) => { onVoiceSelect(v); onClose(); }}
          emotionalOnly={emotionalOnly}
        />
      </BottomDrawer>
    );
  }

  if (!mounted) return null;

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 1060, pointerEvents: "none" }}>
      {/* Backdrop — only dims, doesn't block the modal */}
      <div
        onClick={onClose}
        style={{
          position: "absolute", inset: 0,
          background: "rgba(0,0,0,.25)",
          opacity: visible ? 1 : 0,
          transition: "opacity .25s ease",
          pointerEvents: "auto",
        }}
      />

      {/* Right panel — same dimensions as VoiceChanger */}
      <div
        style={{
          position: "absolute", top: 60, right: 0,
          width: 320, height: "calc(100vh - 60px)",
          background: "#fff",
          borderLeft: `1px solid ${C.n200}`,
          display: "flex", flexDirection: "column",
          transform: visible ? "translateX(0)" : "translateX(100%)",
          transition: "transform .3s cubic-bezier(.32,1,.23,1)",
          pointerEvents: "auto",
          boxShadow: "-4px 0 24px rgba(0,0,0,.08)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "16px 16px 12px",
            borderBottom: `1px solid ${C.n100}`,
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>
            Voice reference
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 28, height: 28, borderRadius: "50%", border: "none",
              background: C.n100, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={13} color="#64748B" />
          </button>
        </div>

        {/* Picker fills remaining height */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <EmbeddedVoicePicker
            selectedVoice={selectedVoice}
            onVoiceSelect={(v) => { onVoiceSelect(v); onClose(); }}
            emotionalOnly={emotionalOnly}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Voice Design detail view ──────────────────────────────────────────────────

const DESIGN_SAMPLE_GRADIENTS = [
  "linear-gradient(135deg,#6366F1,#A855F7)",
  "linear-gradient(135deg,#0EA5E9,#22D3EE)",
  "linear-gradient(135deg,#F59E0B,#EF4444)",
  "linear-gradient(135deg,#10B981,#84CC16)",
];

// Predefined starter prompts — clicking a chip fills the textarea with it.
const DESIGN_SAMPLES = [
  {
    name: "Evil Ogre",
    grad: DESIGN_SAMPLE_GRADIENTS[0],
    prompt: "A deep, booming male voice of a massive evil ogre. Thick, gravelly tone with a resonant, theatrical quality that's both menacing and absurdly silly. Quick, excited pace with erratic bursts of energy.",
  },
  {
    name: "Little Mouse",
    grad: DESIGN_SAMPLE_GRADIENTS[1],
    prompt: "A tiny, squeaky voice of a timid little mouse. High-pitched, fast, breathless, and adorable, with nervous pauses. Perfect audio quality.",
  },
  {
    name: "Southern Woman",
    grad: DESIGN_SAMPLE_GRADIENTS[2],
    prompt: "A warm middle-aged woman with a gentle Southern American drawl. Honeyed, unhurried delivery, friendly and reassuring.",
  },
  {
    name: "News Anchor",
    grad: DESIGN_SAMPLE_GRADIENTS[3],
    prompt: "A crisp, authoritative news anchor voice. Neutral accent, precise diction, measured pace, studio-quality broadcast tone.",
  },
];

// Click-and-drag horizontal scroller (no visible scrollbar) for the sample chips.
function SamplePromptChips({ samples, onPick }) {
  const scrollRef = useRef(null);
  const dragRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, moved: false });

  const onMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { isDown: true, startX: e.pageX - el.offsetLeft, scrollLeft: el.scrollLeft, moved: false };
  };
  const endDrag = () => { dragRef.current.isDown = false; };
  const onMouseMove = (e) => {
    const el = scrollRef.current;
    const state = dragRef.current;
    if (!state.isDown || !el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - state.startX;
    if (Math.abs(walk) > 5) state.moved = true;
    el.scrollLeft = state.scrollLeft - walk;
  };

  return (
    <div
      ref={scrollRef}
      className="voice-sample-chips-scroller"
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      style={{
        display: "flex", alignItems: "center", gap: 7, overflowX: "auto",
        WebkitOverflowScrolling: "touch", cursor: "grab", userSelect: "none",
      }}
    >
      <style>{`
        .voice-sample-chips-scroller { scrollbar-width: none; -ms-overflow-style: none; }
        .voice-sample-chips-scroller::-webkit-scrollbar { display: none; }
      `}</style>
      {samples.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={() => { if (!dragRef.current.moved) onPick(s); }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "5px 12px 5px 5px", background: "#fff",
            border: `1px solid ${C.n200}`, borderRadius: 999,
            cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap", fontFamily: "inherit",
          }}
        >
          <span style={{ width: 20, height: 20, borderRadius: "50%", background: s.grad, flexShrink: 0 }} />
          <span style={{ fontSize: 12.5, fontWeight: 600, color: C.n800 }}>{s.name}</span>
        </button>
      ))}
    </div>
  );
}

const VOICE_DESIGN_PROMPT_MIN_LENGTH = 20;
const VOICE_DESIGN_PROMPT_MAX_LENGTH = 1000;

function VoiceDesignView({ opt, isMobile, org_id, integration_id, onDesignSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);
  const [showVoiceRef, setShowVoiceRef] = useState(false);
  const [referenceVoice, setReferenceVoice] = useState(null);
  const [languageCode, setLanguageCode] = useState("");
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);
  const settingsBtnRef = useRef(null);

  const langOptions = useQwenLanguageOptions();
  const createVoiceDesignMutation = useCreateVoiceDesign();
  const dispatch = useDispatch();

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    if (langOptions.length > 0 && !languageCode) {
      const english = langOptions.find((l) => l.name === "English");
      setLanguageCode(english?.code || langOptions[0].code);
    }
  }, [langOptions, languageCode]);

  const handleSettingsToggle = () => {
    if (showSettings) {
      setShowSettings(false);
    } else {
      const rect = settingsBtnRef.current?.getBoundingClientRect();
      setSettingsAnchor(rect ?? null);
      setShowSettings(true);
    }
  };

  const promptTooShort = prompt.trim().length > 0 && prompt.trim().length < VOICE_DESIGN_PROMPT_MIN_LENGTH;
  const promptTooLong = prompt.trim().length > VOICE_DESIGN_PROMPT_MAX_LENGTH;
  const canSubmit = voiceName.trim() && prompt.trim() && !promptTooShort && !promptTooLong;

  const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
    org_id, feature: "voice-design", enabled: !!canSubmit,
  });

  const startDesigning = async () => {
    if (!voiceName.trim() || !prompt.trim()) return;
    if (promptTooShort) {
      setError(`Prompt is too short. Please use at least ${VOICE_DESIGN_PROMPT_MIN_LENGTH} characters.`);
      return;
    }
    if (promptTooLong) {
      setError(`Prompt is too long. Please keep it under ${VOICE_DESIGN_PROMPT_MAX_LENGTH} characters.`);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const referenceAudioPath = referenceVoice?.audio_sample_path || referenceVoice?.audio || null;

      dispatch(addMixPanelEvent({
        orgId: org_id, integrationId: integration_id, body: {
          eventName: "VOICE_DESIGN_STARTED",
          eventData: {
            tool: "voice_design", // GROW-84: tool on every stage for start->finish comparison
            voice_name: voiceName,
            prompt,
            language_code: languageCode,
            reference_audio_path: referenceAudioPath,
            settings,
          }
        }
      }));

      await createVoiceDesignMutation.mutateAsync({
        org_id, integration_id,
        voice_name: voiceName,
        prompt,
        language_code: languageCode,
        reference_audio_path: referenceAudioPath,
        settings,
      });

      onDesignSuccess?.();
    } catch {
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {error && (
        <div style={{
          background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 7,
          padding: "7px 10px", fontSize: 11.5, color: C.r500,
        }}>
          {error}
        </div>
      )}

      {/* Option header card */}
      <div
        style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          padding: "14px 16px", borderRadius: 12,
          border: "1.5px dashed #C7D2FE", background: "#F5F7FF",
        }}
      >
        <div
          style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: opt.iconBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            marginTop: 1,
          }}
        >
          {opt.icon}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.n900, marginBottom: 3 }}>
            {opt.title}
          </div>
          <div style={{ fontSize: 12.5, color: C.n500, lineHeight: 1.5 }}>
            {opt.detailDescription}
          </div>
        </div>
      </div>

      {/* Voice name + language */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={sectionLabelStyle}>Voice Name</label>
          <input
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            placeholder="Name your voice"
            style={remixInputStyle}
          />
        </div>

        <LanguageSelect value={languageCode} onChange={setLanguageCode} langOptions={langOptions} />
      </div>

      {/* Prompt area */}
      <div
        style={{
          borderRadius: 12, border: `1.5px solid ${C.n200}`, background: "#fff",
          overflow: "hidden",
        }}
      >
        <div style={{ padding: "12px 16px 0", fontSize: 11, fontWeight: 600, color: "#9CA3AF", letterSpacing: "0.04em", textTransform: "uppercase" }}>
          Prompt
        </div>
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder={opt.placeholder}
          rows={3}
          style={{
            width: "100%", border: "none", outline: "none", resize: "none",
            padding: "6px 16px 12px",
            fontSize: 14, color: C.n900, lineHeight: 1.6,
            background: "transparent", fontFamily: "inherit",
          }}
        />

        <div style={{ padding: "0 16px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {promptTooShort ? (
            <span style={{ fontSize: 11, fontWeight: 500, color: C.r500 }}>
              Minimum {VOICE_DESIGN_PROMPT_MIN_LENGTH} characters
            </span>
          ) : <span />}
          <span style={{ fontSize: 11, fontWeight: 500, color: promptTooLong ? C.r500 : "#9CA3AF" }}>
            {prompt.trim().length}/{VOICE_DESIGN_PROMPT_MAX_LENGTH}
          </span>
        </div>

        {/* Predefined starter prompts */}
        <div style={{ padding: "8px 10px", borderTop: `1px solid ${C.n100}`, background: "#F9FAFB" }}>
          <SamplePromptChips samples={DESIGN_SAMPLES} onPick={(s) => setPrompt(s.prompt)} />
        </div>

        {/* Bottom toolbar */}
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 10px", borderTop: `1px solid ${C.n100}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            {/* WIP: Voice reference and Settings are hidden until the /design endpoint
                supports reference voices and settings. Restore when ready. */}
            {/*
            <button
              type="button"
              onClick={() => setShowVoiceRef(true)}
              style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "5px 10px", borderRadius: 8,
                border: `1px solid ${C.n200}`,
                background: referenceVoice ? "#F5F3FF" : "#fff",
                fontSize: 13, fontWeight: 500,
                color: referenceVoice ? C.v500 : "#374151",
                cursor: "pointer",
              }}
            >
              {referenceVoice ? referenceVoice.name : "Voice reference"}
              <ChevronDown size={13} color={referenceVoice ? C.v500 : "#9CA3AF"} />
            </button>

            <button
              ref={settingsBtnRef}
              type="button"
              onClick={handleSettingsToggle}
              style={{
                width: 30, height: 30, borderRadius: 8, border: "none",
                background: showSettings ? C.n100 : "transparent",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <Settings2 size={15} color={showSettings ? C.v500 : "#9CA3AF"} />
            </button>
            */}
          </div>

          {/* WIP: settings popover hidden along with the Settings button above.
          {showSettings && (
            <SettingsPopover
              settings={settings}
              onChange={(key, value) => setSettings((prev) => ({ ...prev, [key]: value }))}
              anchorRect={settingsAnchor}
              onClose={() => setShowSettings(false)}
            />
          )}
          */}

        </div>
      </div>

      {/* Footer actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, paddingTop: 4 }}>
        <FeatureCreditCost orgId={org_id} feature="voice-design" style={{ marginRight: "auto" }} />
        <button
          type="button"
          disabled={!canSubmit || isSubmitting || hasInsufficientCredits}
          onClick={startDesigning}
          title={hasInsufficientCredits ? "Not enough credits to design a voice" : ""}
          style={{
            padding: "8px 18px", borderRadius: 8, border: "none",
            fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            background: canSubmit && !isSubmitting && !hasInsufficientCredits ? C.n900 : "#E5E7EB",
            color: canSubmit && !isSubmitting && !hasInsufficientCredits ? "#fff" : "#9CA3AF",
            cursor: canSubmit && !isSubmitting && !hasInsufficientCredits ? "pointer" : "not-allowed",
          }}
        >
          {isSubmitting ? "Starting…" : "Generate voice"}
        </button>
      </div>

      <VoiceReferenceDrawer
        isOpen={showVoiceRef}
        onClose={() => setShowVoiceRef(false)}
        selectedVoice={referenceVoice}
        onVoiceSelect={setReferenceVoice}
        isMobile={isMobile}
      />
    </div>
  );
}

// ── Voice Remixing ─────────────────────────────────────────────────────────────

const sectionLabelStyle = {
  display: "block", fontSize: 11, fontWeight: 600, color: "#9CA3AF",
  letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 8,
};

const remixInputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "10px 14px", fontSize: 14, color: C.n900,
  border: `1.5px solid ${C.n200}`, borderRadius: 10,
  outline: "none", fontFamily: "inherit", background: "#fff",
};

const refBtnStyle = {
  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5,
  padding: "10px 6px", border: `1px solid ${C.n200}`, borderRadius: 8, background: "#fff",
  cursor: "pointer", fontFamily: "inherit",
};

const refBtnLabelStyle = { fontSize: 11.5, fontWeight: 500, color: "#374151" };

function resolveVoiceAudioUrl(voice) {
  return getVoicePreviewUrl(voice);
}

// Controlled by the caller's `isPlaying`/`onToggle` so that a parent coordinating multiple
// PreviewButtons (e.g. Voice A / Voice B in the remix view) can stop one when another starts.
function PreviewButton({ url, isPlaying, onToggle }) {
  const audioRef = useRef(null);

  useEffect(() => () => audioRef.current?.pause(), []);
  // Stop playback if the underlying source changes or is cleared
  useEffect(() => { audioRef.current?.pause(); audioRef.current = null; }, [url]);
  // Pause when the parent flips isPlaying off (another slot started, dialog closed, etc.)
  useEffect(() => { if (!isPlaying) audioRef.current?.pause(); }, [isPlaying]);

  if (!url) return null;

  const toggle = (e) => {
    e.stopPropagation();
    if (!audioRef.current) {
      audioRef.current = new Audio(url);
      audioRef.current.onended = () => onToggle(false);
    }
    if (isPlaying) {
      audioRef.current.pause();
      onToggle(false);
    } else {
      audioRef.current.play();
      onToggle(true);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      style={{
        width: 22, height: 22, borderRadius: "50%", border: "none",
        background: "#EEF2FF", display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", flexShrink: 0,
      }}
    >
      {isPlaying
        ? <Pause size={10} color={C.v500} fill={C.v500} />
        : <Play size={10} color={C.v500} fill={C.v500} style={{ marginLeft: 1 }} />}
    </button>
  );
}

function MiniAvatar({ letter, color, size = 20 }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: size >= 24 ? 8 : 6, flexShrink: 0,
        background: color, display: "flex", alignItems: "center", justifyContent: "center",
        color: "#fff", fontSize: size >= 24 ? 12 : 10, fontWeight: 700,
      }}
    >
      {letter}
    </div>
  );
}

const VOICE_REF_MAX_SIZE_BYTES = 15 * 1024 * 1024;
const VOICE_REF_MAX_DURATION_SEC = 60;


function readAudioDuration(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = new Audio();
    el.preload = "metadata";
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (!Number.isFinite(el.duration)) {
        reject(new Error("Could not determine file duration."));
        return;
      }
      resolve(el.duration);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read file metadata."));
    };
    el.src = url;
  });
}

function VoiceRefSlot({ label, avatarLetter, avatarColor, value, onChange, onOpenLibrary, bare, isPreviewPlaying, onPreviewToggle }) {
  const fileRef = useRef();
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    setFileError(null);
  }, [value]);

  const { isRecording, duration, startRecording, stopRecording } = useAudioRecorder({
    onRecordingComplete: (blob) => {
      const file = new File([blob], `${label.replace(/\s+/g, "-").toLowerCase()}-recording.wav`, { type: "audio/wav" });
      if (file.size > VOICE_REF_MAX_SIZE_BYTES) {
        setFileError("Recording is too large. Max size is 15MB.");
        return;
      }
      setFileError(null);
      onChange({ type: "record", name: file.name, file });
    },
    maxDuration: VOICE_REF_MAX_DURATION_SEC,
  });

  const handleFile = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > VOICE_REF_MAX_SIZE_BYTES) {
      setFileError("File is too large. Max size is 15MB.");
      return;
    }
    let durationSec;
    try {
      durationSec = await readAudioDuration(f);
    } catch (err) {
      console.error("Could not read duration", err);
      setFileError("Could not verify file duration. Please try a different file.");
      return;
    }
    if (durationSec > VOICE_REF_MAX_DURATION_SEC) {
      setFileError("File is too long. Maximum 1 minute allowed.");
      return;
    }
    setFileError(null);
    onChange({ type: "upload", name: f.name, file: f });
  };

  // Object URLs are local to this slot's file/blob; resolve once per value and revoke on change/unmount
  const previewUrl = useMemo(() => {
    if (!value) return null;
    if (value.file) return URL.createObjectURL(value.file);
    return resolveVoiceAudioUrl(value.voice);
  }, [value]);

  useEffect(() => {
    if (!previewUrl || !value?.file) return undefined;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl, value]);

  return (
    <div style={bare ? undefined : { border: `1.5px solid ${C.n200}`, borderRadius: 12, padding: 14, background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <MiniAvatar letter={avatarLetter} color={avatarColor} size={28} />
        <span style={{ fontSize: 14, fontWeight: 700, color: C.n900 }}>{label}</span>
      </div>

      <input ref={fileRef} type="file" accept="audio/*" onChange={handleFile} style={{ display: "none" }} />

      {isRecording ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "8px 10px",
        }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.r500, flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 12, color: C.r500, fontWeight: 600 }}>
            Recording… {String(Math.floor(duration / 60)).padStart(2, "0")}:{String(duration % 60).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={stopRecording}
            style={{
              padding: "3px 9px", background: C.r500, color: "#fff",
              border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600,
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Stop
          </button>
        </div>
      ) : value ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          background: "#F8FAFC", border: `1px solid ${C.n200}`, borderRadius: 8, padding: "8px 10px",
        }}>
          <Mic size={13} color={C.n500} style={{ flexShrink: 0 }} />
          <span style={{
            flex: 1, fontSize: 12, fontWeight: 600, color: C.n900,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {value.name}
          </span>
          <PreviewButton url={previewUrl} isPlaying={!!isPreviewPlaying} onToggle={onPreviewToggle} />
          <button
            type="button"
            onClick={() => onChange(null)}
            style={{ background: "none", border: "none", cursor: "pointer", display: "flex", padding: 2 }}
          >
            <X size={12} color="#9CA3AF" />
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
          <button type="button" onClick={() => fileRef.current.click()} style={refBtnStyle}>
            <Upload size={14} color={C.n500} />
            <span style={refBtnLabelStyle}>Upload</span>
          </button>
          <button type="button" onClick={startRecording} style={refBtnStyle}>
            <Mic size={14} color={C.n500} />
            <span style={refBtnLabelStyle}>Record</span>
          </button>
          <button type="button" onClick={onOpenLibrary} style={refBtnStyle}>
            <Search size={14} color={C.n500} />
            <span style={refBtnLabelStyle}>Library</span>
          </button>
        </div>
      )}
      {!value && !isRecording && (
        <p style={{ margin: "6px 0 0", fontSize: 10.5, color: "#9CA3AF", textAlign: "center" }}>
          Max 15MB or 1 min
        </p>
      )}
      {fileError && (
        <p style={{ margin: "6px 0 0", fontSize: 11.5, color: C.r500, fontWeight: 600 }}>{fileError}</p>
      )}
    </div>
  );
}

// Resolves a VoiceRefSlot descriptor down to an S3 key/URL the backend can pass straight
// to the worker: a library voice's existing path, or a freshly uploaded/recorded file
// goes through a presigned-URL -> S3 upload flow.
async function resolveMixSource(descriptor, { org_id, integration_id, getPresignedUrlMutation }) {
  if (descriptor.type === "library") {
    return descriptor.voice?.audio_sample_path || descriptor.voice?.audio || null;
  }

  const presignedData = await getPresignedUrlMutation.mutateAsync({
    org_id, integration_id,
    fileName: descriptor.file.name,
    contentType: descriptor.file.type,
  });
  await uploadToS3(presignedData.signed_url, descriptor.file);

  const bucketUrl = process.env.REACT_APP_CLONED_VOICE_BUCKET_URL || process.env.REACT_APP_AUDIO_PREVIEW_URL;
  return new URL(`${bucketUrl}/${presignedData.url}`).pathname.substring(1);
}

function VoiceRemixView({ isMobile, org_id, integration_id, onCancel, onMixSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [voiceName, setVoiceName] = useState("");
  const [voiceA, setVoiceA] = useState(null);
  const [voiceB, setVoiceB] = useState(null);
  const [blend, setBlend] = useState(50);
  const [languageCode, setLanguageCode] = useState("");
  const [activeSlot, setActiveSlot] = useState(null); // 'A' | 'B' | null
  const [playingSlot, setPlayingSlot] = useState(null); // 'A' | 'B' | null — only one preview plays at a time
  const [error, setError] = useState(null);

  const langOptions = useQwenLanguageOptions({ mixOnly: true });
  const getPresignedUrlMutation = useGetPresignedUrl();
  const createVoiceMixMutation = useCreateVoiceMix();
  const dispatch = useDispatch();

  useEffect(() => {
    if (langOptions.length > 0 && !languageCode) {
      const english = langOptions.find((l) => l.name === "English");
      setLanguageCode(english?.code || langOptions[0].code);
    }
  }, [langOptions, languageCode]);

  const canSubmit = voiceName.trim() && voiceA && voiceB;

  const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
    org_id, feature: "voice-mix", enabled: !!canSubmit,
  });

  const handleLibrarySelect = (voice) => {
    const descriptor = { type: "library", name: voice.name, voice };
    if (activeSlot === "A") setVoiceA(descriptor);
    if (activeSlot === "B") setVoiceB(descriptor);
    setActiveSlot(null);
  };

  const startMixing = async () => {
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const [voiceASource, voiceBSource] = await Promise.all([
        resolveMixSource(voiceA, { org_id, integration_id, getPresignedUrlMutation }),
        resolveMixSource(voiceB, { org_id, integration_id, getPresignedUrlMutation }),
      ]);

      dispatch(addMixPanelEvent({
        orgId: org_id, integrationId: integration_id, body: {
          eventName: "VOICE_MIX_STARTED",
          eventData: {
            tool: "voice_remix", // GROW-84: tool on every stage for start->finish comparison
            voice_name: voiceName,
            language_code: languageCode,
            voice_a_source: voiceASource,
            voice_b_source: voiceBSource,
            voice_a_is_library: voiceA.type === "library",
            voice_b_is_library: voiceB.type === "library",
            voice_a_weight: 100 - blend,
            voice_b_weight: blend,
          }
        }
      }));

      await createVoiceMixMutation.mutateAsync({
        org_id, integration_id,
        voice_name: voiceName,
        language_code: languageCode,
        voice_a_source: voiceASource,
        voice_b_source: voiceBSource,
        voice_a_weight: 100 - blend,
        voice_b_weight: blend,
        voice_a_is_library: voiceA.type === "library",
        voice_b_is_library: voiceB.type === "library",
      });

      onMixSuccess?.();
    } catch {
      setError("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {error && (
        <div style={{
          background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 7,
          padding: "7px 10px", fontSize: 11.5, color: C.r500,
        }}>
          {error}
        </div>
      )}

      {/* Voice name + language */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={sectionLabelStyle}>Voice Name</label>
          <input
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            placeholder="e.g. My Remix Voice"
            style={remixInputStyle}
          />
        </div>

        <LanguageSelect value={languageCode} onChange={setLanguageCode} langOptions={langOptions} />
      </div>

      {/* Voice references */}
      <div>
        <span style={sectionLabelStyle}>Voice References</span>
        <div style={{ border: `1.5px solid ${C.n200}`, borderRadius: 12, background: "#fff" }}>
          <div style={{ padding: 14 }}>
            <VoiceRefSlot
              bare
              label="Voice A" avatarLetter="A" avatarColor={C.v500}
              value={voiceA} onChange={setVoiceA} onOpenLibrary={() => setActiveSlot("A")}
              isPreviewPlaying={playingSlot === "A"}
              onPreviewToggle={(playing) => setPlayingSlot(playing ? "A" : null)}
            />
          </div>
          <div style={{ borderTop: `1px solid ${C.n100}`, padding: 14 }}>
            <VoiceRefSlot
              bare
              label="Voice B" avatarLetter="B" avatarColor="#EC4899"
              value={voiceB} onChange={setVoiceB} onOpenLibrary={() => setActiveSlot("B")}
              isPreviewPlaying={playingSlot === "B"}
              onPreviewToggle={(playing) => setPlayingSlot(playing ? "B" : null)}
            />
          </div>
        </div>
      </div>

      {/* Blend */}
      <div style={{ background: "#F8FAFC", border: `1px solid ${C.n100}`, borderRadius: 12, padding: "14px 16px" }}>
        <span style={{ ...sectionLabelStyle, marginBottom: 10, display: "block" }}>Blend</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <MiniAvatar letter="A" color={C.v500} />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: C.n900 }}>Voice A</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.v500 }}>{100 - blend}%</span>
          </div>
          <input
            type="range" min={0} max={100} value={blend}
            onChange={(e) => setBlend(Number(e.target.value))}
            style={{ flex: 1, accentColor: C.n900, cursor: "pointer" }}
          />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flexShrink: 0 }}>
            <MiniAvatar letter="B" color="#EC4899" />
            <span style={{ fontSize: 12.5, fontWeight: 600, color: C.n900 }}>Voice B</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#EC4899" }}>{blend}%</span>
          </div>
        </div>
      </div>

      {/* Footer actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, paddingTop: 12, borderTop: `1px solid ${C.n100}` }}>
        <FeatureCreditCost orgId={org_id} feature="voice-mix" style={{ marginRight: "auto" }} />
        <button
          type="button"
          onClick={onCancel}
          style={{
            padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.n200}`,
            background: "#F8FAFC", color: "#374151", fontSize: 13, fontWeight: 600,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canSubmit || isSubmitting || hasInsufficientCredits}
          onClick={startMixing}
          title={hasInsufficientCredits ? "Not enough credits to create this remix" : ""}
          style={{
            padding: "8px 18px", borderRadius: 8, border: "none",
            fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            background: canSubmit && !isSubmitting && !hasInsufficientCredits ? C.n900 : "#E5E7EB",
            color: canSubmit && !isSubmitting && !hasInsufficientCredits ? "#fff" : "#9CA3AF",
            cursor: canSubmit && !isSubmitting && !hasInsufficientCredits ? "pointer" : "not-allowed",
          }}
        >
          {isSubmitting ? "Starting…" : "Create remix"}
        </button>
      </div>

      <VoiceReferenceDrawer
        isOpen={activeSlot !== null}
        onClose={() => setActiveSlot(null)}
        selectedVoice={activeSlot === "A" ? voiceA?.voice : voiceB?.voice}
        onVoiceSelect={handleLibrarySelect}
        isMobile={isMobile}
      />
    </div>
  );
}

// ── Placeholder detail views ──────────────────────────────────────────────────

function PlaceholderView({ opt }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div
        style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          padding: "14px 16px", borderRadius: 12,
          border: "1.5px dashed #C7D2FE", background: "#F5F7FF",
        }}
      >
        <div
          style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: opt.iconBg,
            display: "flex", alignItems: "center", justifyContent: "center",
            marginTop: 1,
          }}
        >
          {opt.icon}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.n900, marginBottom: 3 }}>
            {opt.title}
          </div>
          <div style={{ fontSize: 12.5, color: C.n500, lineHeight: 1.5 }}>
            {opt.detailDescription}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "40px 16px", borderRadius: 12,
          border: `1.5px dashed ${C.n200}`, background: "#FAFAFA",
          textAlign: "center", color: "#9CA3AF", fontSize: 13,
        }}
      >
        Coming soon
      </div>
    </div>
  );
}

// ── Detail view router ────────────────────────────────────────────────────────

export function OptionDetailView({ optionId, isMobile, org_id, integration_id, onCancel, onSuccess }) {
  const opt = OPTIONS.find((o) => o.id === optionId);
  if (!opt) return null;
  if (optionId === "design") return <VoiceDesignView opt={opt} isMobile={isMobile} org_id={org_id} integration_id={integration_id} onDesignSuccess={onSuccess} />;
  if (optionId === "clone") return <VoiceCloneView org_id={org_id} integration_id={integration_id} onCloneSuccess={onSuccess} />;
  if (optionId === "remix") return <VoiceRemixView onCancel={onCancel} isMobile={isMobile} org_id={org_id} integration_id={integration_id} onMixSuccess={onSuccess} />;
  return <PlaceholderView opt={opt} />;
}

// ── Dialog shell ──────────────────────────────────────────────────────────────

function DialogHeader({ showBack, title, onBack, onClose }) {
  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "20px 24px 0",
      }}
    >
      {showBack ? (
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            background: "none", border: "none", cursor: "pointer",
            fontSize: 13, fontWeight: 600, color: C.n500, padding: 0,
          }}
        >
          <ArrowLeft size={14} />
          Back to Create voice
        </button>
      ) : (
        <span style={{ fontSize: 18, fontWeight: 700, color: C.n900 }}>
          {title ?? "Create voice"}
        </span>
      )}
      <button
        type="button"
        onClick={onClose}
        style={{
          width: 30, height: 30, borderRadius: "50%", border: "none",
          background: C.n100, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <X size={14} color="#64748B" />
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   createdCount?: number,
 *   totalAllowed?: number,
 *   org_id?: string,
 *   integration_id?: string,
 *   initialOption?: "design" | "remix" | null,
 *   lockOption?: boolean,
 *   onSuccess?: () => void,
 * }} props
 */
export default function CreateVoiceDialog({
  isOpen,
  onClose,
  createdCount = 0,
  totalAllowed = 3,
  org_id,
  integration_id,
  // Optionally jump straight to a path's form (e.g. "design"/"remix")
  // instead of showing the option-list screen first.
  initialOption = null,
  // Skips the option-list/back-navigation — for callers with their own picker UI.
  lockOption = false,
  onSuccess,
}) {
  const isMobile = useIsMobile();
  const [selectedOption, setSelectedOption] = useState(initialOption);

  // Reset drill-down when dialog closes; re-seed from initialOption on open.
  useEffect(() => {
    if (!isOpen) setSelectedOption(null);
    else if (initialOption) setSelectedOption(initialOption);
  }, [isOpen, initialOption]);

  const handleOptionSelect = (id) => setSelectedOption(id);
  const handleBack = () => setSelectedOption(null);
  const showBack = !!selectedOption && !lockOption;
  const selectedOpt = OPTIONS.find((o) => o.id === selectedOption);
  const title = lockOption && selectedOpt ? selectedOpt.title : "Create voice";

  const content = selectedOption ? (
    <OptionDetailView
      optionId={selectedOption}
      isMobile={isMobile}
      org_id={org_id}
      integration_id={integration_id}
      onCancel={handleBack}
      onSuccess={onSuccess}
    />
  ) : (
    <OptionList onSelect={handleOptionSelect} />
  );

  if (isMobile) {
    return (
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        title={
          showBack ? (
            <button
              type="button"
              onClick={handleBack}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "none", border: "none", cursor: "pointer",
                fontSize: 14, fontWeight: 600, color: C.n500, padding: 0,
              }}
            >
              <ArrowLeft size={14} />
              Back to Create voice
            </button>
          ) : (
            title
          )
        }
      >
        {content}
      </BottomDrawer>
    );
  }

  const dialogClassName = `create-voice-dialog${selectedOption === "clone" ? " create-voice-dialog--wide" : ""}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm" closeOnOverlayClick animation={false} centered={false} scrollable={false} className={dialogClassName}>
      <style>{`
        /* Positioned directly via fixed + transform, independent of .modal's own
           flex/inline-block centering machinery (which legacy _modal.scss overrides in ways
           that fought every attempt to fix this via .modal/.modal-dialog alone). */
        .modal-dialog.create-voice-dialog {
          position: fixed !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important;
          margin: 0 !important;
          max-width: 480px !important;
          width: 95% !important;
          max-height: calc(100vh - 3.5rem) !important;
          min-height: 0 !important;
          height: auto !important;
          display: block !important;
        }
        .modal-dialog.create-voice-dialog.create-voice-dialog--wide {
          max-width: 640px !important;
        }
        .create-voice-dialog .modal-content {
          max-height: calc(100vh - 3.5rem);
          overflow-y: auto;
        }
      `}</style>
      <DialogHeader
        showBack={showBack}
        title={title}
        onBack={handleBack}
        onClose={onClose}
      />
      <div style={{ padding: "20px 24px 24px" }}>
        {content}
      </div>
    </Modal>
  );
}
